import React, { useState, useEffect, useCallback } from "react";
import {
  Send,
  Loader2,
  AlertCircle,
  BarChart3,
  Mail,
  Truck,
  CheckCircle,
  RotateCcw,
  DollarSign,
  TrendingUp,
  ChevronRight,
  Calendar,
  Users,
  Eye
} from "lucide-react";
import {
  fetchCampaigns,
  fetchCampaignDashboard,
  CAMPAIGN_STATUS_CONFIG,
  formatCurrency,
  formatDate
} from "../../services/MailingListService";

/**
 * CampaignsView - Campaign dashboard with metrics and history
 */
const CampaignsView = ({ userEmail, userMeta, onViewCampaign }) => {
  const [dashboard, setDashboard] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load dashboard metrics and campaigns
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [dashboardData, campaignsData] = await Promise.all([
        fetchCampaignDashboard(),
        fetchCampaigns({ limit: 20 })
      ]);

      setDashboard(dashboardData);
      setCampaigns(campaignsData.campaigns || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Metric card component
  const MetricCard = ({ icon: Icon, label, value, subValue, color = "sky" }) => (
    <div className="p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg bg-${color}-100 dark:bg-${color}-900/30 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
        </div>
        <div className="text-sm text-slate-500 dark:text-slate-400">{label}</div>
      </div>
      <div className="text-2xl font-bold dark:text-white text-slate-900">{value}</div>
      {subValue && (
        <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">{subValue}</div>
      )}
    </div>
  );

  // Campaign status badge
  const StatusBadge = ({ status }) => {
    const config = CAMPAIGN_STATUS_CONFIG[status] || CAMPAIGN_STATUS_CONFIG.draft;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.bgClass} ${config.textClass}`}>
        {config.label}
      </span>
    );
  };

  // Delivery progress bar
  const DeliveryProgress = ({ metrics }) => {
    const total = metrics.sent || 0;
    if (total === 0) return null;

    const delivered = metrics.delivered || 0;
    const inTransit = metrics.in_transit || 0;
    const returned = metrics.returned || 0;

    const deliveredPct = (delivered / total) * 100;
    const transitPct = (inTransit / total) * 100;
    const returnedPct = (returned / total) * 100;

    return (
      <div className="space-y-1">
        <div className="flex h-2 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-700">
          <div
            className="bg-green-500"
            style={{ width: `${deliveredPct}%` }}
            title={`Delivered: ${delivered}`}
          />
          <div
            className="bg-amber-500"
            style={{ width: `${transitPct}%` }}
            title={`In Transit: ${inTransit}`}
          />
          <div
            className="bg-red-500"
            style={{ width: `${returnedPct}%` }}
            title={`Returned: ${returned}`}
          />
        </div>
        <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
          <span className="flex items-center gap-1">
            <CheckCircle className="w-3 h-3 text-green-500" />
            {delivered} delivered
          </span>
          <span className="flex items-center gap-1">
            <Truck className="w-3 h-3 text-amber-500" />
            {inTransit} in transit
          </span>
          <span className="flex items-center gap-1">
            <RotateCcw className="w-3 h-3 text-red-500" />
            {returned} returned
          </span>
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 text-sky-600 animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold dark:text-white text-slate-900 mb-2">
            Failed to load campaigns
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-4">{error}</p>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-500 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!dashboard || dashboard.total_campaigns === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <Send className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
            No Campaigns Yet
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            Create your first campaign from a mailing list to start sending postcards.
          </p>
          <p className="text-sm text-slate-400 dark:text-slate-500">
            Go to Mailing Lists tab → Select a list → Click "Create Campaign"
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Dashboard metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={Send}
          label="Total Campaigns"
          value={dashboard.total_campaigns}
          color="sky"
        />
        <MetricCard
          icon={Mail}
          label="Postcards Sent"
          value={dashboard.total_sent?.toLocaleString() || 0}
          color="blue"
        />
        <MetricCard
          icon={CheckCircle}
          label="Delivered"
          value={dashboard.total_delivered?.toLocaleString() || 0}
          subValue={`${dashboard.delivery_rate || 0}% rate`}
          color="green"
        />
        <MetricCard
          icon={DollarSign}
          label="Total Spend"
          value={formatCurrency(dashboard.total_spend || 0)}
          color="amber"
        />
      </div>

      {/* Campaigns list */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800">
          <h2 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-sky-600" />
            Campaign History
          </h2>
        </div>

        <div className="divide-y divide-slate-200 dark:divide-slate-800">
          {campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-medium dark:text-white text-slate-900">
                    {campaign.name}
                  </h3>
                  <div className="flex items-center gap-3 mt-1 text-sm text-slate-500 dark:text-slate-400">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(campaign.sent_at || campaign.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      {campaign.metrics?.total_contacts?.toLocaleString() || 0} contacts
                    </span>
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-3.5 h-3.5" />
                      {formatCurrency(campaign.metrics?.actual_cost || campaign.metrics?.estimated_cost || 0)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={campaign.status} />
                  {onViewCampaign && (
                    <button
                      onClick={() => onViewCampaign(campaign)}
                      className="p-2 text-slate-400 hover:text-sky-600 dark:hover:text-sky-400 rounded-lg hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-colors"
                      title="View details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Delivery progress for sent campaigns */}
              {campaign.status !== "draft" && campaign.metrics && (
                <DeliveryProgress metrics={campaign.metrics} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Lob tracking info */}
      <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-sky-600 mt-0.5" />
          <div>
            <h4 className="font-medium dark:text-white text-slate-900">Real-Time Tracking</h4>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Delivery status updates automatically via Lob webhooks. Postcards typically take 3-5 business days for delivery after printing.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignsView;
